import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import client, { apiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Search, Plus, Minus, Trash2, ShoppingCart, Wallet, Star, Printer,
  Share2, Receipt, Lock, Loader2, X, CheckCircle2,
} from "lucide-react";

export default function POS() {
  const { user, isAllBranch } = useAuth();
  const { effectiveBranch, branches, currentBranchName } = useBranch();

  const [shift, setShift] = useState(undefined); // undefined=loading
  const [openCash, setOpenCash] = useState("");
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [cart, setCart] = useState([]);
  const [billDiscount, setBillDiscount] = useState(0);
  const [payOpen, setPayOpen] = useState(false);
  const [paid, setPaid] = useState("");
  const [allowNeg, setAllowNeg] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authEmail, setAuthEmail] = useState("");
  const [authPass, setAuthPass] = useState("");
  const [discountAuthorized, setDiscountAuthorized] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("walkin");
  const [payMethod, setPayMethod] = useState("cash");
  const isRM = ["super_admin", "admin"].includes(user.role);
  const [custDialog, setCustDialog] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", phone: "" });
  const searchRef = useRef(null);

  const loadShift = useCallback(() => {
    client.get("/shifts/active", { params: effectiveBranch ? { branch_id: effectiveBranch } : {} })
      .then((r) => setShift(r.data))
      .catch(() => setShift(null));
  }, [effectiveBranch]);

  const loadProducts = useCallback(() => {
    client.get("/products", { params: effectiveBranch ? { branch_id: effectiveBranch } : {} })
      .then((r) => setProducts(r.data.filter((p) => p.active !== false)));
  }, [effectiveBranch]);

  useEffect(() => {
    if (isAllBranch && !effectiveBranch) { setShift(null); return; }
    loadShift();
    loadProducts();
    client.get("/categories").then((r) => setCats(r.data));
    client.get("/customers").then((r) => setCustomers(r.data));
  }, [effectiveBranch, isAllBranch, loadShift, loadProducts]);

  const filtered = useMemo(() => {
    let list = products;
    if (activeCat === "fav") list = list.filter((p) => p.is_favorite);
    else if (activeCat !== "all") list = list.filter((p) => p.category_id === activeCat);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(s) || (p.barcode || "").includes(s) || (p.sku || "").toLowerCase().includes(s));
    }
    return list;
  }, [products, activeCat, q]);

  const unitPrice = (p, unitName) => {
    const u = p.units.find((x) => x.name === unitName) || p.units[0];
    return u ? u.sell_price : 0;
  };

  const addToCart = (p) => {
    if (p.region_priced === false) {
      toast.error(`Harga jual "${p.name}" belum diatur untuk region ini`);
      return;
    }
    const unit = p.base_unit;
    setCart((c) => {
      const idx = c.findIndex((i) => i.product_id === p.id && i.unit === unit);
      if (idx >= 0) {
        const copy = [...c];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + 1 };
        return copy;
      }
      return [...c, {
        product_id: p.id, name: p.name, unit, units: p.units,
        qty: 1, sell_price: unitPrice(p, unit), discount: 0,
        stock_qty: p.stock_qty, base_unit: p.base_unit,
      }];
    });
  };

  // F2 fokus ke input, lalu scan barcode + Enter untuk langsung tambah ke keranjang
  const handleSearchKey = (e) => {
    if (e.key !== "Enter") return;
    const term = q.trim();
    if (!term) return;
    const byCode = products.find(
      (p) => (p.barcode && p.barcode === term) || (p.sku && p.sku.toLowerCase() === term.toLowerCase())
    );
    const target = byCode || (filtered.length === 1 ? filtered[0] : null);
    if (target) {
      addToCart(target);
      setQ("");
    } else {
      toast.error(`Barcode/produk "${term}" tidak ditemukan`);
    }
  };

  const updateLine = (i, patch) => setCart((c) => c.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeLine = (i) => setCart((c) => c.filter((_, idx) => idx !== i));
  const changeUnit = (i, unitName) => {
    setCart((c) => c.map((it, idx) => {
      if (idx !== i) return it;
      const u = it.units.find((x) => x.name === unitName);
      if (u && u.priced === false) {
        toast.error(`Satuan "${unitName}" belum memiliki harga di region ini`);
        return it;
      }
      return { ...it, unit: unitName, sell_price: u ? u.sell_price : it.sell_price };
    }));
  };

  const subtotal = cart.reduce((s, i) => s + i.qty * i.sell_price, 0);
  const lineDiscounts = cart.reduce((s, i) => s + (Number(i.discount) || 0), 0);
  const total = Math.max(0, subtotal - lineDiscounts - (Number(billDiscount) || 0));
  const totalDiscount = lineDiscounts + (Number(billDiscount) || 0);
  const bigDiscount = subtotal > 0 && totalDiscount / subtotal > 0.1;
  const needsAuth = bigDiscount && user.role === "cashier" && !discountAuthorized;

  // keyboard shortcuts
  useEffect(() => {
    const h = (e) => {
      if (e.key === "F2") { e.preventDefault(); searchRef.current?.focus(); }
      if (e.key === "F8") { e.preventDefault(); document.getElementById("bill-discount")?.focus(); }
      if (e.key === "F12") { e.preventDefault(); if (cart.length && shift) openPay(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  const openShift = async () => {
    try {
      const r = await client.post("/shifts/open", {
        branch_id: effectiveBranch || user.branch_id,
        opening_cash: Number(openCash) || 0,
      });
      setShift(r.data);
      toast.success("Shift dibuka");
    } catch (e) { toast.error(apiError(e)); }
  };

  const openPay = () => {
    if (needsAuth) { setAuthOpen(true); return; }
    setPaid(String(total));
    setPayMethod("cash");
    setPayOpen(true);
  };

  const verifyAuth = async () => {
    try {
      const { data } = await client.post("/auth/login", { email: authEmail.trim(), password: authPass });
      if (["super_admin", "admin", "branch_head"].includes(data.user.role)) {
        setDiscountAuthorized(true);
        setAuthOpen(false);
        setAuthEmail(""); setAuthPass("");
        toast.success(`Diskon diotorisasi oleh ${data.user.name}`);
        setPaid(String(total));
        setPayOpen(true);
      } else {
        toast.error("Akun tidak berwenang mengotorisasi diskon");
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitSale = async () => {
    setSubmitting(true);
    const cust = customers.find((c) => c.id === customerId);
    try {
      const r = await client.post("/sales", {
        branch_id: effectiveBranch || user.branch_id,
        shift_id: shift?.id,
        customer_id: customerId === "walkin" ? null : customerId,
        customer_name: cust ? cust.name : "",
        customer_phone: cust ? cust.phone || "" : "",
        items: cart.map((i) => ({ product_id: i.product_id, unit: i.unit, qty: Number(i.qty), sell_price: Number(i.sell_price), discount: Number(i.discount) || 0 })),
        discount_total: Number(billDiscount) || 0,
        paid: payMethod === "credit" ? 0 : Number(paid),
        payment_method: payMethod,
        override_negative: allowNeg,
      });
      setReceipt(r.data);
      setPayOpen(false);
      setCart([]); setBillDiscount(0); setPaid(""); setAllowNeg(false); setDiscountAuthorized(false); setCustomerId("walkin"); setPayMethod("cash");
      loadProducts(); loadShift();
      toast.success("Transaksi berhasil");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  const addCustomer = async () => {
    if (!newCust.name.trim()) { toast.error("Nama pelanggan wajib diisi"); return; }
    try {
      const r = await client.post("/customers", { name: newCust.name, phone: newCust.phone, address: "" });
      setCustomers((c) => [...c, r.data]);
      setCustomerId(r.data.id);
      setCustDialog(false); setNewCust({ name: "", phone: "" });
      toast.success("Pelanggan disimpan");
    } catch (e) { toast.error(apiError(e)); }
  };

  const printReceipt = () => window.print();

  const shareWA = () => {
    const r = receipt;
    const lines = [
      `*TOKO KU* - ${currentBranchName}`,
      `No: ${r.number}`,
      r.customer_name ? `Pelanggan: ${r.customer_name}` : null,
      "".padEnd(24, "-"),
      ...r.items.map((i) => `${i.name} ${i.qty}x${rupiah(i.sell_price)} = ${rupiah(i.subtotal)}`),
      "".padEnd(24, "-"),
      `Total: ${rupiah(r.total)}`,
      `Bayar: ${rupiah(r.paid)}`,
      `Kembali: ${rupiah(r.change)}`,
      "Terima kasih telah berbelanja 🙏",
    ].filter(Boolean);
    let phone = (r.customer_phone || "").replace(/\D/g, "");
    if (phone.startsWith("0")) phone = "62" + phone.slice(1);
    const base = phone ? `https://wa.me/${phone}` : "https://wa.me/";
    window.open(`${base}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  // ---- render guards ----
  if (isAllBranch && !effectiveBranch) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="text-center">
          <ShoppingCartIcon />
          <p className="mt-2 text-muted-foreground">Pilih cabang di bagian atas untuk mulai transaksi.</p>
        </div>
      </div>
    );
  }

  if (shift === undefined) {
    return <div className="flex h-full items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  if (shift === null) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="w-full max-w-md rounded-xl border bg-white p-8 shadow-sm animate-in-up">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-blue-50"><Wallet className="h-6 w-6 text-primary" /></div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Buka Shift Kasir</h2>
          <p className="mt-1 text-sm text-muted-foreground">Masukkan modal awal kas untuk memulai sesi penjualan di {currentBranchName}.</p>
          <div className="mt-5 space-y-2">
            <Label htmlFor="opening">Modal Awal (Kas)</Label>
            <Input id="opening" type="number" value={openCash} onChange={(e) => setOpenCash(e.target.value)}
              placeholder="0" className="h-12 font-mono text-lg" data-testid="opening-cash-input" />
          </div>
          <Button className="mt-5 h-12 w-full text-base" onClick={openShift} data-testid="open-shift-btn">Buka Shift</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col lg:flex-row">
      {/* LEFT: products */}
      <div className="flex min-h-0 flex-1 flex-col border-r">
        <div className="border-b bg-white p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={handleSearchKey}
              placeholder="Scan barcode / cari produk… (F2), Enter untuk tambah" className="h-12 pl-10 text-base" data-testid="pos-search" />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {shift && (
              <div className="mr-auto flex items-center gap-3 rounded-lg bg-slate-900 px-3 py-1.5 text-white" data-testid="pos-shift-stat">
                <span className="text-[11px] text-slate-300">Penjualan shift ini</span>
                <span className="font-mono text-sm font-bold">{rupiah(shift.sales_total || 0)}</span>
                <span className="text-[11px] text-slate-400">· {shift.sales_count || 0} trx</span>
              </div>
            )}
            <CatChip active={activeCat === "all"} onClick={() => setActiveCat("all")} label="Semua" />
            <CatChip active={activeCat === "fav"} onClick={() => setActiveCat("fav")} label="★ Favorit" />
            {cats.map((c) => (
              <CatChip key={c.id} active={activeCat === c.id} onClick={() => setActiveCat(c.id)} label={c.name} />
            ))}
          </div>
        </div>
        <ScrollArea className="flex-1 p-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
            {filtered.map((p) => {
              const out = p.stock_qty <= 0;
              const noPrice = p.region_priced === false;
              return (
                <button key={p.id} onClick={() => addToCart(p)} data-testid={`product-${p.id}`}
                  className={`group relative flex flex-col rounded-lg border bg-white p-3 text-left transition-transform duration-200 hover:-translate-y-1 hover:border-primary hover:shadow-md ${noPrice ? "opacity-60" : ""}`}>
                  {p.is_favorite && <Star className="absolute right-2 top-2 h-4 w-4 fill-amber-400 text-amber-400" />}
                  <div className="flex-1">
                    <div className="line-clamp-2 text-sm font-medium leading-snug">{p.name}</div>
                  </div>
                  {noPrice ? (
                    <div className="mt-2 flex items-center gap-1 text-xs font-semibold text-amber-600" data-testid={`product-noprice-${p.id}`}>
                      <Lock className="h-3.5 w-3.5" /> Harga belum diatur
                    </div>
                  ) : (
                    <div className="mt-2 font-mono text-base font-semibold text-primary">{rupiah(unitPrice(p, p.base_unit))}</div>
                  )}
                  <div className="mt-1 flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground">/ {p.base_unit}</span>
                    <span className={out ? "font-semibold text-rose-600" : "text-muted-foreground"}>
                      {out ? "Habis" : `stok ${p.stock_qty}`}
                    </span>
                  </div>
                </button>
              );
            })}
            {!filtered.length && <div className="col-span-full py-12 text-center text-muted-foreground">Produk tidak ditemukan</div>}
          </div>
        </ScrollArea>
      </div>

      {/* RIGHT: cart */}
      <div className="flex w-full flex-col bg-white lg:w-[380px]">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2 font-heading text-lg font-semibold">
            <ShoppingCart className="h-5 w-5" /> Keranjang
          </div>
          <Badge variant="secondary">{cart.length} item</Badge>
        </div>

        <ScrollArea className="flex-1">
          <div className="divide-y">
            {cart.map((it, i) => (
              <div key={`${it.product_id}-${it.unit}`} className="p-3" data-testid={`cart-line-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-medium">{it.name}</div>
                  <button onClick={() => removeLine(i)} className="text-muted-foreground hover:text-destructive" data-testid={`cart-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex items-center rounded-md border">
                    <button className="flex h-9 w-9 items-center justify-center hover:bg-slate-100" onClick={() => updateLine(i, { qty: Math.max(1, it.qty - 1) })} data-testid={`cart-minus-${i}`}><Minus className="h-4 w-4" /></button>
                    <input type="number" value={it.qty} onChange={(e) => updateLine(i, { qty: Math.max(1, Number(e.target.value) || 1) })}
                      className="h-9 w-12 border-x text-center font-mono text-sm outline-none" data-testid={`cart-qty-${i}`} />
                    <button className="flex h-9 w-9 items-center justify-center hover:bg-slate-100" onClick={() => updateLine(i, { qty: it.qty + 1 })} data-testid={`cart-plus-${i}`}><Plus className="h-4 w-4" /></button>
                  </div>
                  {it.units.length > 1 ? (
                    <Select value={it.unit} onValueChange={(v) => changeUnit(i, v)}>
                      <SelectTrigger className="h-9 w-24" data-testid={`cart-unit-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {it.units.map((u) => <SelectItem key={u.name} value={u.name} disabled={u.priced === false}>{u.name}{u.priced === false ? " (belum diatur)" : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex h-9 items-center rounded-md border px-3 text-sm text-muted-foreground">{it.unit}</div>
                  )}
                  <div className="ml-auto font-mono text-sm font-semibold">{rupiah(it.qty * it.sell_price)}</div>
                </div>
              </div>
            ))}
            {!cart.length && (
              <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
                <ShoppingCart className="mb-2 h-10 w-10 opacity-30" />
                <p className="text-sm">Keranjang kosong</p>
                <p className="text-xs">Klik produk untuk menambah</p>
              </div>
            )}
          </div>
        </ScrollArea>

        <div className="border-t p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="bill-discount" className="whitespace-nowrap text-xs text-muted-foreground">Diskon Nota (F8)</Label>
            <Input id="bill-discount" type="number" value={billDiscount} onChange={(e) => setBillDiscount(e.target.value)}
              className="h-9 font-mono" placeholder="0" data-testid="bill-discount" />
          </div>
          {bigDiscount && (
            <div className="flex items-center gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-700">
              <Lock className="h-3.5 w-3.5" /> Diskon &gt;10% butuh otorisasi atasan
            </div>
          )}
          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{rupiah(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Diskon</span><span className="font-mono text-rose-600">-{rupiah(totalDiscount)}</span></div>
            <div className="flex justify-between border-t pt-1 text-lg font-bold"><span>Total</span><span className="font-mono text-primary" data-testid="cart-total">{rupiah(total)}</span></div>
          </div>
          <Button className="h-14 w-full text-lg" disabled={!cart.length} onClick={openPay} data-testid="pos-pay-btn">
            <Wallet className="mr-2 h-5 w-5" /> Bayar (F12)
          </Button>
        </div>
      </div>

      {/* PAYMENT dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{payMethod === "credit" ? "Kas Bon (Kredit)" : "Pembayaran Tunai"}</DialogTitle>
            <DialogDescription>Total belanja {rupiah(total)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {isRM && (
              <div className="flex gap-2" data-testid="pos-paymethod">
                <button onClick={() => setPayMethod("cash")} className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${payMethod === "cash" ? "border-primary bg-primary text-white" : "hover:bg-slate-50"}`} data-testid="pos-method-cash">Tunai</button>
                <button onClick={() => setPayMethod("credit")} className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${payMethod === "credit" ? "border-amber-500 bg-amber-500 text-white" : "hover:bg-slate-50"}`} data-testid="pos-method-credit">Kas Bon (Kredit)</button>
              </div>
            )}
            <div className="rounded-lg bg-slate-900 p-4 text-center text-white">
              <div className="text-xs text-slate-400">Total Tagihan</div>
              <div className="font-mono text-3xl font-bold">{rupiah(total)}</div>
            </div>
            <div className="space-y-2">
              <Label>Pelanggan {payMethod === "credit" ? "(wajib)" : "(opsional)"}</Label>
              <div className="flex gap-2">
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger className="flex-1" data-testid="pos-customer-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="walkin">Pelanggan Umum</SelectItem>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" onClick={() => setCustDialog(true)} data-testid="pos-add-customer-btn"><Plus className="h-4 w-4" /></Button>
              </div>
              {payMethod === "credit" && customerId === "walkin" && <p className="text-xs text-rose-600" data-testid="pos-credit-warn">Pilih pelanggan terdaftar untuk Kas Bon.</p>}
            </div>
            {payMethod === "credit" ? (
              <div className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="pos-credit-info">
                Transaksi dicatat sebagai <b>piutang</b> pelanggan (belum dibayar). Stok tetap berkurang, tidak masuk kas.
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="paid">Uang Diterima</Label>
                  <Input id="paid" type="number" value={paid} onChange={(e) => setPaid(e.target.value)}
                    className="h-14 font-mono text-2xl" autoFocus data-testid="pay-amount" />
                  <div className="flex flex-wrap gap-2">
                    {[total, 50000, 100000, 150000, 200000].map((v, idx) => (
                      <button key={idx} onClick={() => setPaid(String(v))}
                        className="rounded-full border px-3 py-1 text-xs font-medium transition-colors hover:bg-primary hover:text-white" data-testid={`quick-cash-${idx}`}>
                        {v === total ? "Uang Pas" : rupiah(v)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-emerald-50 px-4 py-3">
                  <span className="text-sm font-medium text-emerald-700">Kembalian</span>
                  <span className="font-mono text-xl font-bold text-emerald-700" data-testid="change-amount">{rupiah(Math.max(0, (Number(paid) || 0) - total))}</span>
                </div>
              </>
            )}
            {isAllBranch && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={allowNeg} onChange={(e) => setAllowNeg(e.target.checked)} data-testid="allow-negative" />
                Izinkan stok negatif (override, transaksi ditandai)
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Batal</Button>
            <Button onClick={submitSale} disabled={submitting || (payMethod === "credit" ? customerId === "walkin" : (Number(paid) || 0) < total)} data-testid="confirm-pay-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (payMethod === "credit" ? "Simpan Kas Bon" : "Selesaikan & Cetak")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AUTH discount dialog */}
      <Dialog open={authOpen} onOpenChange={setAuthOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock className="h-5 w-5" /> Otorisasi Diskon</DialogTitle>
            <DialogDescription>Diskon melebihi 10%. Minta Kepala Cabang/Regional Manager untuk login.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Email atasan" value={authEmail} onChange={(e) => setAuthEmail(e.target.value)} data-testid="auth-email" />
            <Input type="password" placeholder="Kata sandi" value={authPass} onChange={(e) => setAuthPass(e.target.value)} data-testid="auth-pass" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuthOpen(false)}>Batal</Button>
            <Button onClick={verifyAuth} data-testid="auth-confirm">Otorisasi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ADD CUSTOMER dialog */}
      <Dialog open={custDialog} onOpenChange={setCustDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Tambah Pelanggan</DialogTitle>
            <DialogDescription>Simpan data pelanggan untuk struk & kirim WhatsApp.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Nama</Label><Input value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} data-testid="new-customer-name" /></div>
            <div className="space-y-2"><Label>No. WhatsApp</Label><Input value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} placeholder="08xxxxxxxxxx" data-testid="new-customer-phone" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustDialog(false)}>Batal</Button>
            <Button onClick={addCustomer} data-testid="save-customer-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RECEIPT dialog */}
      <Dialog open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)}>
        <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600"><CheckCircle2 className="h-5 w-5" /> Transaksi Selesai</DialogTitle>
          </DialogHeader>
          {receipt && (
            <>
              <div className="shrink-0 text-center text-xs text-muted-foreground">Struk termal 58mm</div>
              <div className="flex flex-1 justify-center overflow-y-auto rounded-md bg-slate-50 py-2">
                <ReceiptView receipt={receipt} branchName={currentBranchName} width="58" cashier={user.name} />
              </div>
              <DialogFooter className="shrink-0 flex-col gap-2 sm:flex-row">
                <Button variant="outline" className="flex-1" onClick={shareWA} data-testid="share-wa-btn"><Share2 className="mr-2 h-4 w-4" /> WhatsApp</Button>
                <Button variant="outline" className="flex-1" onClick={printReceipt} data-testid="print-btn"><Printer className="mr-2 h-4 w-4" /> Cetak</Button>
                <Button className="flex-1" onClick={() => setReceipt(null)} data-testid="new-trx-btn"><Receipt className="mr-2 h-4 w-4" /> Transaksi Baru</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {receipt && (
        <div id="receipt-print" className="hidden print:block">
          <ReceiptView receipt={receipt} branchName={currentBranchName} width="58" cashier={user.name} plain />
        </div>
      )}
    </div>
  );
}

function CatChip({ active, onClick, label }) {
  return (
    <button onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors duration-200 ${active ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
      {label}
    </button>
  );
}

function ShoppingCartIcon() {
  return <ShoppingCart className="mx-auto h-12 w-12 text-muted-foreground opacity-40" />;
}

export function ReceiptView({ receipt, branchName, width, cashier, plain }) {
  const w = width === "58" ? "220px" : "300px";
  const r = receipt;
  return (
    <div style={{ width: w }} className={plain ? "font-mono text-[12px] text-black" : "rounded border border-dashed border-slate-400 bg-white p-4 font-mono text-[12px] text-slate-800"}>
      <div className="text-center">
        <div className="text-base font-bold">TOKO KU</div>
        <div>{branchName}</div>
        <div className="text-[10px]">{new Date(r.created_at).toLocaleString("id-ID")}</div>
      </div>
      <div className="my-1 border-t border-dashed border-slate-400" />
      <div>No : {r.number}</div>
      <div>Kasir : {cashier}</div>
      {r.customer_name && <div>Pelanggan : {r.customer_name}</div>}
      <div className="my-1 border-t border-dashed border-slate-400" />
      {r.items.map((i, idx) => (
        <div key={idx} className="mb-1">
          <div>{i.name}</div>
          <div className="flex justify-between">
            <span>{i.qty} {i.unit} x {Math.round(i.sell_price).toLocaleString("id-ID")}</span>
            <span>{Math.round(i.subtotal).toLocaleString("id-ID")}</span>
          </div>
        </div>
      ))}
      <div className="my-1 border-t border-dashed border-slate-400" />
      <Row k="Subtotal" v={r.subtotal} />
      {r.discount_total > 0 && <Row k="Diskon" v={-r.discount_total} />}
      <div className="flex justify-between font-bold"><span>TOTAL</span><span>{Math.round(r.total).toLocaleString("id-ID")}</span></div>
      {r.payment_method === "credit" ? (
        <div className="text-center font-bold">** KAS BON / BELUM DIBAYAR **</div>
      ) : (
        <>
          <Row k="Tunai" v={r.paid} />
          <Row k="Kembali" v={r.change} />
        </>
      )}
      <div className="my-1 border-t border-dashed border-slate-400" />
      <div className="text-center text-[11px]">Terima kasih telah berbelanja</div>
      <div className="text-center text-[10px]">~ TOKO KU ~</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between">
      <span>{k}</span>
      <span>{Math.round(v).toLocaleString("id-ID")}</span>
    </div>
  );
}
